import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Bookmark, Share2, MapPin, Mountain, Clock,
  Star, Play, Send, MessageCircle, Droplets, Flag, Camera, Circle,
} from 'lucide-react';
import { fetchRoute, voteRoute, commentOnRoute } from '../../api/move';
import type { MoveRouteDetail } from '../../api/move';
import { API_BASE } from '../../config';
import RouteMap from '../../components/Move/RouteMap';
import styles from '../../styles/Move.module.css';

interface Comment {
  comment_id: number;
  user_id: number;
  fullName: string;
  texto: string;
  created_at: string;
}

interface Hito {
  km?: number;
  nombre?: string;
  descripcion?: string;
  tipo?: 'agua' | 'meta' | 'foto' | 'mirador' | 'descanso' | string;
}

function formatDistance(m: number) {
  if (m >= 1000) return `${(m / 1000).toFixed(1)}` ;
  return `${(m / 1000).toFixed(2)}`;
}

function difficultyLabel(d: number) {
  if (d <= 1) return 'Muy fácil';
  if (d === 2) return 'Fácil';
  if (d === 3) return 'Moderada';
  if (d === 4) return 'Difícil';
  return 'Exigente';
}

function imageUrl(filename?: string | null) {
  if (!filename) return null;
  if (filename.startsWith('http')) return filename;
  return `${API_BASE}/api/move_images/${filename}`;
}

function hitoIcon(tipo?: string) {
  switch (tipo) {
    case 'agua': return <Droplets size={16} />;
    case 'meta': return <Flag size={16} />;
    case 'foto':
    case 'mirador': return <Camera size={16} />;
    default: return <Circle size={16} />;
  }
}

/**
 * Construye una polilínea simple (perfil de elevación) a partir de un valor único de desnivel.
 * Hasta tener un perfil real desde geometry, esto da forma visual coherente.
 */
function buildElevationPath(desnivel: number, distancia: number): { d: string; area: string } {
  const W = 320;
  const H = 80;
  const points = 24;
  const intensity = Math.min(1, desnivel / 600);
  const arr: number[] = [];
  for (let i = 0; i < points; i++) {
    const t = i / (points - 1);
    // Onda con un pico hacia el medio y desnivel proporcional
    const wave = Math.sin(t * Math.PI) * intensity;
    const noise = Math.sin(t * 11) * 0.08 * intensity;
    arr.push(Math.max(0, wave + noise));
  }
  const max = Math.max(...arr, 0.001);
  const coords = arr.map((v, i) => {
    const x = (i / (points - 1)) * W;
    const y = H - (v / max) * (H - 8) - 4;
    return [x, y] as const;
  });
  const d = coords.map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`)).join(' ');
  const area = `${d} L ${W} ${H} L 0 ${H} Z`;
  // distancia se mantiene en la firma para futura escala real
  void distancia;
  return { d, area };
}

export default function RouteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState<MoveRouteDetail | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const routeId = Number(id);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRoute(routeId);
      setRoute(data.route);
      setComments(data.comments);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Error cargando ruta');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (routeId) load(); }, [routeId]);

  function flashToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }

  async function handleVote(estrellas: number) {
    setBusy(true);
    try { await voteRoute(routeId, estrellas); flashToast(`Has votado con ${estrellas}★`); await load(); }
    finally { setBusy(false); }
  }

  async function handleSendComment() {
    if (!newComment.trim()) return;
    setBusy(true);
    try {
      await commentOnRoute(routeId, newComment.trim());
      setNewComment('');
      await load();
    } finally { setBusy(false); }
  }

  async function handleStartRoute() {
    if (!routeId) return;
    navigate(`/move/activity/new?route_id=${routeId}`);
  }

  const elevation = useMemo(() => {
    if (!route) return { d: '', area: '' };
    return buildElevationPath(route.desnivel_m || 0, route.distancia_m || 0);
  }, [route]);

  if (loading) return <div className={`move-app ${styles.page}`}><p className={styles.empty}>Cargando…</p></div>;
  if (error || !route) {
    return (
      <div className={`move-app ${styles.page}`}>
        <p className={styles.empty}>{error || 'Ruta no encontrada'}</p>
        <div style={{ padding: '0 1.25rem' }}>
          <button className={styles.btnGhost} style={{ width: '100%' }} onClick={() => navigate('/move/routes')}>
            <ArrowLeft size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Volver
          </button>
        </div>
      </div>
    );
  }

  const img = imageUrl(route.imagen_principal);
  const hitos: Hito[] = Array.isArray(route.hitos) ? route.hitos : [];

  return (
    <div className={`move-app ${styles.page}`} style={{ paddingTop: 0 }}>
      {/* Hero con imagen y acciones flotantes */}
      <div className={styles.heroImage}>
        {img ? <img src={img} alt={route.titulo} /> : <div className={styles.heroFallback} />}
        <div className={styles.floatActions}>
          <button className={styles.floatBtn} onClick={() => navigate('/move/routes')} aria-label="Volver">
            <ArrowLeft size={18} />
          </button>
          <div className={styles.floatGroup}>
            <button className={styles.floatBtn} onClick={() => flashToast('Guardado en favoritos')} aria-label="Guardar">
              <Bookmark size={18} />
            </button>
            <button className={styles.floatBtn} onClick={() => flashToast('Enlace copiado')} aria-label="Compartir">
              <Share2 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido principal sobre el hero */}
      <div className={styles.detailContent}>
        <div className={styles.detailMetaRow}>
          <span className={`${styles.tag} ${styles.tagPrimary}`}>{route.tipo}</span>
          <span className={`${styles.tag} ${styles.tagAccent}`}>{difficultyLabel(route.dificultad)}</span>
          <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Star size={14} fill="hsl(42 95% 50%)" color="hsl(42 95% 50%)" />
            <span style={{ fontSize: 14, fontWeight: 700 }}>{Number(route.rating_avg).toFixed(1)}</span>
            <span style={{ fontSize: 12, color: 'var(--move-muted-fg)' }}>({route.votos_count})</span>
          </div>
        </div>

        <h1 className={styles.detailTitle}>{route.titulo}</h1>
        {route.descripcion && <p className={styles.detailDesc}>{route.descripcion}</p>}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 18 }}>
          <div className={styles.statCard}>
            <MapPin size={16} color="hsl(0 72% 44%)" style={{ margin: '0 auto' }} />
            <p className={styles.statValue}>{formatDistance(route.distancia_m)}</p>
            <p className={styles.statLabel}>km</p>
          </div>
          <div className={styles.statCard}>
            <Mountain size={16} color="hsl(18 94% 54%)" style={{ margin: '0 auto' }} />
            <p className={styles.statValue}>{route.desnivel_m}</p>
            <p className={styles.statLabel}>m D+</p>
          </div>
          <div className={styles.statCard}>
            <Clock size={16} color="hsl(190 78% 42%)" style={{ margin: '0 auto' }} />
            <p className={styles.statValue}>{route.duracion_min}</p>
            <p className={styles.statLabel}>min</p>
          </div>
        </div>

        {/* Mapa real Leaflet */}
        <div style={{ marginTop: 22 }}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: 10 }}>Mapa de la ruta</h2>
          <RouteMap
            geometry={route.geometry}
            fallbackPoint={
              route.lat_inicio != null && route.lng_inicio != null
                ? { lat: Number(route.lat_inicio), lng: Number(route.lng_inicio) }
                : null
            }
            height={220}
          />
        </div>

        {/* Elevation profile */}
        <div style={{ marginTop: 22 }}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: 10 }}>Perfil de elevación</h2>
          <div className={styles.card} style={{ padding: 12 }}>
            <svg viewBox="0 0 320 80" className={styles.elevWrap} preserveAspectRatio="none">
              <defs>
                <linearGradient id="moveElev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(18 94% 54%)" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="hsl(18 94% 54%)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={elevation.area} fill="url(#moveElev)" />
              <path d={elevation.d} stroke="hsl(18 94% 54%)" strokeWidth="2.5" fill="none" />
            </svg>
          </div>
        </div>

        {/* Hitos / checkpoints */}
        {hitos.length > 0 && (
          <div style={{ marginTop: 22 }}>
            <h2 className={styles.sectionTitle} style={{ marginBottom: 10 }}>Hitos</h2>
            <div className={styles.cardList} style={{ gap: 8 }}>
              {hitos.map((h, i) => (
                <div key={i} className={styles.hitoItem}>
                  <div className={styles.hitoIcon}>{hitoIcon(h.tipo)}</div>
                  <div className={styles.hitoContent}>
                    <p className={styles.hitoTitle}>{h.nombre || `Hito ${i + 1}`}</p>
                    {h.descripcion && <p className={styles.hitoSub}>{h.descripcion}</p>}
                  </div>
                  {typeof h.km === 'number' && <span className={styles.hitoKm}>km {h.km}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Voto */}
        <div style={{ marginTop: 22 }}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: 10 }}>Tu valoración</h2>
          <div className={styles.card}>
            <div style={{ display: 'flex', gap: 6 }}>
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  disabled={busy}
                  onClick={() => handleVote(n)}
                  className={styles.pill}
                  style={{ flex: 1, justifyContent: 'center', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <Star size={13} fill="hsl(42 95% 50%)" color="hsl(42 95% 50%)" />
                  <span>{n}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Comentarios */}
        <div style={{ marginTop: 22 }}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: 10 }}>
            <MessageCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Comentarios
          </h2>
          <div className={styles.card} style={{ padding: 10 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className={styles.input}
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Comparte tu experiencia…"
                style={{ flex: 1 }}
              />
              <button
                disabled={busy || !newComment.trim()}
                onClick={handleSendComment}
                className={styles.btnPrimary}
                style={{ padding: '0 16px' }}
                aria-label="Enviar"
              >
                <Send size={16} />
              </button>
            </div>
          </div>

          <div className={styles.cardList} style={{ marginTop: 10 }}>
            {comments.length === 0 && <div className={styles.empty}>Sé el primero en comentar</div>}
            {comments.map(c => (
              <div key={c.comment_id} className={styles.card}>
                <p className={styles.cardTitle} style={{ fontSize: 13 }}>{c.fullName}</p>
                <p style={{ fontSize: 14, color: 'var(--move-fg)', margin: '6px 0 0' }}>{c.texto}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <button
          className={styles.ctaBig}
          onClick={handleStartRoute}
          disabled={busy}
        >
          <Play size={18} fill="currentColor" />
          Añadir progreso manual
        </button>
      </div>

      {/* Toast inline */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--move-secondary)',
          color: '#fff',
          padding: '10px 18px',
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 600,
          boxShadow: 'var(--move-shadow-md)',
          zIndex: 100,
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
